import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDebounce } from "/app/src/hooks";
import { locationService, zoneService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Zone as ZoneType, Location as LocationType } from "/app/src/models";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import useSetting from "/app/src/components/settings/setting";
import {
  buildExplorerColumns,
  isColumnSelected,
  toggleExplorerColumn,
} from "/app/src/components/explorer/helpers/columns";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
} from "@heroui/react";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import { useAuthState } from "/app/src/contexts/authentication";

/**
 * Component that renders the locations explorer page.
 *
 * @returns {JSX.Element} The locations component.
 */
export default function Locations() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [fillFilter, setFillFilter] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const debouncedSearchTerm = useDebounce(searchString, 1000);

  /**
   * Navigates to the location detail page.
   *
   * @param {number} id - The unique identifier for the location.
   */
  const goToLocation = useCallback(
    (id: number) => {
      navigate(`/explorer/locations/${id}`);
    },
    [navigate],
  );

  /**
   * Updates the fill filter state.
   *
   * @param {*} value - The new value for the fill filter.
   */
  const settingFillFilter = useCallback((value) => {
    setFillFilter(value.target.value);
  }, []);

  /**
   * Updates the zone filter state.
   *
   * @param {*} value - The new value for the zone filter.
   */
  const settingZoneFilter = useCallback((value) => {
    setZoneFilter(value.target.value);
  }, []);

  const { user } = useAuthState();
  const columnHelper = createColumnHelper<LocationType>();

  const availableColumns = [
    { id: "currentQuantity", label: t("translation:current_quantity") },
    { id: "capacityUsed", label: t("translation:capacity_used") },
    { id: "capacityReserved", label: t("translation:capacity_reserved") },
    { id: "markedFull", label: t("translation:marked_full") },
    { id: "zoneId", label: t("translation:zone") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "location", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "location",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            header: t("translation:name"),
            cell: (info) => info.getValue(),
          });
        case "currentQuantity":
          return columnHelper.accessor("currentQuantity", {
            id: "currentQuantity",
            header: t("translation:current_quantity"),
            cell: (info) => info.getValue(),
          });
        case "capacityUsed":
          return columnHelper.accessor("capacityUsed", {
            id: "capacityUsed",
            header: t("translation:capacity_used"),
            cell: (info) => info.getValue(),
          });
        case "capacityReserved":
          return columnHelper.accessor("capacityReserved", {
            id: "capacityReserved",
            header: t("translation:capacity_reserved"),
            cell: (info) => info.getValue(),
          });
        case "markedFull":
          return columnHelper.accessor("isMarkedFull", {
            id: "isMarkedFull",
            header: t("translation:marked_full"),
            cell: (info) => {
              if (info.getValue() === 0) {
                return t("translation:no");
              }
              return t("translation:yes");
            },
          });
        case "zone":
          return columnHelper.accessor("zoneId", {
            id: "zoneId",
            header: t("translation:zone"),
            cell: (info) => info.getValue(),
          });
        default:
          return null;
      }
    };

    return buildExplorerColumns(settingValue, "location", makeColumn, "name");
  }, [settingValue, t, columnHelper]);

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  const { data: zones } = useQuery({
    queryKey: ["zones"],
    queryFn: () => {
      return zoneService.getAll();
    },
    initialData: { zones: [] },
    select: (data: { zones: ZoneType[] }) => {
      return data.zones;
    },
  });

  const {
    data: locations,
    count: locationsCount,
    isFetching,
  } = usePaginatedData<LocationType>({
    queryKey: ["locations"],
    searchString: debouncedSearchTerm,
    sort,
    page,
    pageSize,
    service: locationService,
    options: {
      isMarkedFull: fillFilter,
      zoneId: zoneFilter,
    },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: locationsCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:locations")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:locations") },
        ]}
      />
      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
          <Select
            size="md"
            onChange={settingZoneFilter}
            className="w-[300px]"
            placeholder={t("translation:filter_by_zone")}
            items={[{ id: "all", name: t("translation:all") }, ...zones]}
          >
            {(item) => (
              <SelectItem key={item.id.toString()}>{item.name}</SelectItem>
            )}
          </Select>

          <Select
            size="md"
            selectedKeys={fillFilter ? [fillFilter] : []}
            onChange={settingFillFilter}
            className="w-[300px]"
            placeholder={t("translation:filter_by_marked_full")}
          >
            <SelectItem key="all">{t("translation:all")}</SelectItem>
            <SelectItem key="1">{t("translation:full")}</SelectItem>
            <SelectItem key="0">{t("translation:not_full")}</SelectItem>
          </Select>
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Table
        loading={isFetching}
        rows={locations}
        tableColumns={columns}
        length={locationsCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToLocation}
        emptyText={t("translation:no_locations_found")}
      />
    </div>
  );
}
